import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
/** One row of the international eSIM tariff grid. */
export const TravelEsimTariffSchema = z.object({
    id: z.string(),
    /** Destination as written in the grid, French (e.g. "Argentine"). */
    country: z.string(),
    /** English name from the grid (e.g. "ARGENTINA"), when provided. */
    countryEn: z.string().nullable(),
    /** Plan label as written in the grid (e.g. "Illimité", "3GB"). */
    planLabel: z.string(),
    /** Data allowance in GB; null means unlimited. */
    dataLimitGb: z.number().int().nullable(),
    durationDays: z.number().int(),
    /** Price excluding VAT, in cents, per eSIM. */
    priceCents: z.number().int(),
});
/**
 * A loaded version of the grid; the catalog serves the highest version and
 * older ones are price history (orders snapshot their own copy).
 */
export const TravelEsimTariffGridSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    label: z.string(),
    createdAt: z.string(),
});
/** What an admin enters for a tariff; the plan label derives from the data. */
export const TravelEsimTariffInputSchema = z.object({
    country: z.string().trim().min(1),
    countryEn: z.string().trim().min(1).nullish(),
    /** Data allowance in GB; null means unlimited. */
    dataLimitGb: z.number().int().positive().nullable(),
    durationDays: z.number().int().positive(),
    /** Price excluding VAT, in cents, per eSIM. */
    priceCents: z.number().int().nonnegative(),
});
export const UpdateTravelEsimTariffBodySchema = TravelEsimTariffInputSchema.partial();
export const CurrentTravelEsimTariffGridSchema = z.object({
    grid: TravelEsimTariffGridSchema.nullable(),
    tariffs: z.array(TravelEsimTariffSchema),
});
const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a YYYY-MM-DD date');
/** Dates are mandatory: the offer is priced for the exact stay. */
export const TravelEsimQuoteQuerySchema = z
    .object({
    country: z.string().min(1),
    departureDate: IsoDateSchema,
    returnDate: IsoDateSchema,
})
    .refine((q) => q.returnDate >= q.departureDate, {
    message: 'returnDate must be on or after departureDate',
});
/**
 * The single offer of a destination for a stay: the richest data tier,
 * priced as the cheapest cover of the stay. The plans Eiko has to buy are
 * an internal matter (kept on the order line), not exposed here.
 */
export const TravelEsimQuoteSchema = z.object({
    country: z.string(),
    /** Data for the whole stay in GB (chained plans add up), null = unlimited. */
    dataLimitGb: z.number().int().nullable(),
    planLabel: z.string(),
    departureDate: z.string(),
    returnDate: z.string(),
    /** Inclusive day count of the stay. */
    stayDurationDays: z.number().int(),
    /** Days the eSIM will actually cover from the departure (>= stay). */
    coveredDays: z.number().int(),
    /** HT, cents, for the whole stay. */
    totalPriceCents: z.number().int(),
    /** HT, cents, total spread over the stay. */
    pricePerDayCents: z.number().int(),
});
/**
 * International eSIM tariff grid: the admin read of the current version, and
 * the org-scoped reads the catalog tab is built on. The grid is loaded by
 * script (versioned rows), not through the API.
 */
/** An eSIM a beneficiary already holds, as the order dialog shows it. */
export const TravelEsimSummarySchema = z.object({
    id: z.string(),
    esimNumber: z.string().nullable(),
    qrCodeSentAt: z.string().nullable(),
    createdAt: z.string(),
});
export const LookupBeneficiaryTravelEsimsQuerySchema = z
    .object({
    lineId: z.string().min(1).optional(),
    email: z.string().email().optional(),
})
    .refine((q) => !!q.lineId || !!q.email, {
    message: 'lineId or email is required',
});
export const travelEsimContract = c.router({
    lookupBeneficiaryTravelEsims: {
        method: 'GET',
        path: '/organizations/:organizationId/travel-esims/lookup',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        query: LookupBeneficiaryTravelEsimsQuerySchema,
        responses: { 200: z.array(TravelEsimSummarySchema) },
        summary: 'The eSIMs a beneficiary (fleet line or e-mail) already holds, most recent first',
    },
    getCurrentTariffGrid: {
        method: 'GET',
        path: '/admin/travel-esim/tariffs',
        responses: { 200: CurrentTravelEsimTariffGridSchema },
        summary: 'Current version of the international eSIM tariff grid (admin)',
    },
    createTravelEsimTariff: {
        method: 'POST',
        path: '/admin/travel-esim/tariffs',
        body: TravelEsimTariffInputSchema,
        responses: { 201: TravelEsimTariffSchema },
        summary: 'Add a tariff to the current grid (admin)',
    },
    updateTravelEsimTariff: {
        method: 'PATCH',
        path: '/admin/travel-esim/tariffs/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: UpdateTravelEsimTariffBodySchema,
        responses: { 200: TravelEsimTariffSchema },
        summary: 'Change a tariff of the current grid (admin)',
    },
    deleteTravelEsimTariff: {
        method: 'DELETE',
        path: '/admin/travel-esim/tariffs/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: { 200: z.object({ success: z.literal(true) }) },
        summary: 'Remove a tariff from the current grid (admin)',
    },
    listDestinations: {
        method: 'GET',
        path: '/organizations/:organizationId/travel-esim/destinations',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: z.array(z.string()) },
        summary: 'Destinations covered by the international eSIM grid',
    },
    getQuote: {
        method: 'GET',
        path: '/organizations/:organizationId/travel-esim/quote',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        query: TravelEsimQuoteQuerySchema,
        responses: { 200: TravelEsimQuoteSchema },
        summary: 'The international eSIM offer for a destination and a stay',
    },
});
